import { useCallback, useState } from "react";
import axios from "axios";
import { environment } from "../../environment";
import { useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import {
    DCBulkLicensingResult,
    DCDesignFilters,
    DCDesignLicensing,
    DCDesignLicensingInput,
} from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const licensingFields = `id design_id model percentage_value flat_amount flat_interval currency`;

// #region graphqlRequest
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, {
        headers: { "X-Wallmates-Auth": authToken }
    });
    if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
    return data.data;
};

export interface SetLicensingResult {
    licensing: DCDesignLicensing;
    is_catalog_design: boolean;
    catalog_published: boolean;
}

// #region useDesignLicensing
/**
 * Catalog licensing price mutations. Setting a price flips `is_catalog_design`
 * server-side, so both mutations return the fresh design flags for the caller
 * to fold back into local state without a reload.
 */
export const useDesignLicensing = () => {
    const [, authToken] = useLogin();
    const [, setSnackbar] = useSnackbar();

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // #region setLicensing
    const setLicensing = useCallback(async (
        designId: number | string,
        input: DCDesignLicensingInput
    ): Promise<SetLicensingResult | null> => {
        setSaving(true);
        setError(null);
        const query = `mutation($designId: ID!, $input: DCDesignLicensingInput!) {
            setDCDesignLicensing(designId: $designId, input: $input) {
                licensing { ${licensingFields} }
                is_catalog_design
                catalog_published
            }
        }`;
        try {
            const data = await graphqlRequest(authToken, query, { designId: String(designId), input });
            return data?.setDCDesignLicensing as SetLicensingResult;
        } catch (err: any) {
            const message = err?.message || 'Failed to save licensing price';
            setError(message);
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        } finally {
            setSaving(false);
        }
    }, [authToken, setSnackbar]);

    // #region clearLicensing
    const clearLicensing = useCallback(async (designId: number | string): Promise<boolean> => {
        setSaving(true);
        setError(null);
        const query = `mutation($designId: ID!) { clearDCDesignLicensing(designId: $designId) }`;
        try {
            await graphqlRequest(authToken, query, { designId: String(designId) });
            return true;
        } catch (err: any) {
            const message = err?.message || 'Failed to remove licensing price';
            setError(message);
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return false;
        } finally {
            setSaving(false);
        }
    }, [authToken, setSnackbar]);

    return { saving, error, setLicensing, clearLicensing };
};

/**
 * Hard cap enforced by the backend. Mirrored here so the dialog can explain the
 * limit instead of letting the request come back a 400.
 */
export const MAX_BULK_LICENSING_DESIGNS = 250;

// #region bulkSetLicensing
/**
 * Applies ONE licensing price to many designs and (unless `publish` is false)
 * publishes every eligible one to the licensing catalog in a single call.
 *
 * Deliberately a plain async helper, not a hook: `useDesignLicensing` is
 * per-design and hook-bound, while this is called from a dialog's submit
 * handler with an id list resolved at click time.
 *
 * Partial failure is normal — inspect `skipped`/`failed` rather than treating a
 * non-empty list as an error.
 */
export const bulkSetLicensing = async (
    authToken: string | null,
    brandId: number | string,
    designIds: (number | string)[],
    input: DCDesignLicensingInput,
    publish = true
): Promise<DCBulkLicensingResult> => {
    const query = `mutation($brand_id: ID!, $design_ids: [ID!]!, $input: DCDesignLicensingInput!, $publish: Boolean) {
        bulkSetDCDesignLicensing(brand_id: $brand_id, design_ids: $design_ids, input: $input, publish: $publish) {
            total
            priced
            published
            skipped { design_id design_title reason }
            failed { design_id design_title reason }
        }
    }`;
    const data = await graphqlRequest(authToken, query, {
        brand_id: String(brandId),
        design_ids: designIds.map(String),
        input,
        publish
    });
    return data?.bulkSetDCDesignLicensing as DCBulkLicensingResult;
};

// #region fetchDesignIdsMatchingFilters
/**
 * Resolves EVERY design id matching the given filters, so "select all N
 * matching" spans designs the client never paged in. Ids are resolved at submit
 * time, not at click time — a design created in between is included.
 */
export const fetchDesignIdsMatchingFilters = async (
    authToken: string | null,
    brandId: number | string,
    filters: Partial<DCDesignFilters>
): Promise<number[]> => {
    const query = `query($brand_id: ID!, $filters: DCDesignFilters) {
        dcDesignIdsMatchingFilters(brand_id: $brand_id, filters: $filters)
    }`;
    const data = await graphqlRequest(authToken, query, {
        brand_id: String(brandId),
        filters: {
            status: filters.status?.length ? filters.status : undefined,
            pattern_type: filters.pattern_type?.length ? filters.pattern_type : undefined,
            released: filters.released || undefined,
            title_search: filters.title_search || undefined,
            needs_attention: filters.needs_attention || undefined,
            default_wallpaper_scale: filters.default_wallpaper_scale?.length ? filters.default_wallpaper_scale : undefined,
            created_by: filters.created_by?.length ? filters.created_by : undefined,
            licensing_status: filters.licensing_status?.length ? filters.licensing_status : undefined,
        }
    });
    return ((data?.dcDesignIdsMatchingFilters as (string | number)[]) || []).map(Number);
};
